import { X } from "lucide-react";
import * as React from "react";
import { useCallback, useEffect, useLayoutEffect } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";

import { useScrollLock } from "../../hooks/useScrollLock";
import { useSwipeToDismiss } from "../../hooks/useSwipeToDismiss";
import { Loader } from "../ui/Loader";

/* istanbul ignore next */
const WorkPage = React.lazy(() => import("../../pages/WorkPage"));
/* istanbul ignore next */
const SegmentPage = React.lazy(() => import("../../pages/SegmentPage"));

const WorkModal: React.FC = () => {
  const navigate = useNavigate();
  const close = useCallback(() => navigate(-1), [navigate]);
  const location = useLocation();
  const { ref, onTouchStart, onTouchMove, onTouchEnd, reset } =
    useSwipeToDismiss(close);
  useScrollLock();

  // Swiping back from an episode lands on its work, still in the modal.
  useLayoutEffect(reset, [location.key, reset]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [close]);

  return (
    // The scrim is a pointer's way out; Escape and Close are the keyboard's.
    <div
      className="modal-backdrop"
      role="presentation"
      onClick={(e) => e.target === e.currentTarget && close()}
    >
      <div
        className="modal"
        ref={ref}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <button
          className="modal-close btn btn-ghost btn-sm icon-only"
          onClick={close}
          aria-label="Close"
        >
          <X size="1em" aria-hidden="true" />
        </button>
        <React.Suspense fallback={<Loader block />}>
          <Routes>
            <Route path="/works/:id" element={<WorkPage />} />
            <Route
              path="/works/:id/segments/:segId"
              element={<SegmentPage />}
            />
          </Routes>
        </React.Suspense>
      </div>
    </div>
  );
};

export default WorkModal;
